/* =========================================================================
   report.tsx — Board / IC pack report component
   ========================================================================= */

import { useState, useMemo, useRef, useId, useEffect } from "react";
import { useResizeObserver } from "../../hooks";
import * as Fmt from "../../format";
import { DEFAULT_CPI_ASSUMPTION } from "../../types";
import { useScenario } from "../../state";
import { Icons, KPICard, Section, FlagItem } from "../../components";
import { Tornado } from "../../charts";
import { Model } from "../../model";
import { computeMonthly } from "../../monthly";
import { useToast } from "../../toast";
import { catchmentFor } from "../../census";
import { useAuthSafe } from "../../auth-context";
import { canExportExcel } from "../../entitlements";
import { KVCard } from "./components";
import { useExcelWorker } from "../../workers/hooks";
import { buildChartsSheet } from "../../export-excel";
import {
  applyBoardWorkbookLayout,
  SHEET_ORDER,
  reorderWorksheets,
  applyPageSetup,
  normalizeFonts,
} from "../../excel/workbook";
import { Workbook } from "exceljs";
import { buildDealRisks } from "../../deal-risks";
import {
  GEARED_REFERENCE_DISCLOSURE,
  getDeveloperDecision,
  getOperatorBoardDecisionContext,
  getOperatorDealRecommendation,
} from "../../decision-metrics";
import {
  BOARD_COST_BASIS_LABELS,
  BOARD_COST_COPY,
  BOARD_COST_STATUS_LABELS,
  buildBoardCostViewModel,
} from "../../presentation/board-cost-view-model";
import {
  gearedRefinanceExitLabel,
  gearedRefinanceExitFootnote,
  developerEquityRequiredLvrFootnote,
  OPERATOR_CONTRIBUTION_BOARD_NOTE,
  OPERATOR_IRR_CONVERGED_FOOTNOTE,
  operatorNpvDiscountFootnote,
  operatorPaybackFootnote,
  operatorPaybackProseClause,
  rampOpeningLiquidityProseLead,
  RAMP_OPENING_LIQUIDITY_SUPPORT_SUFFIX,
  modelHorizonYearFootnote,
  operatorTotalTaxFootnote,
  y1OccupancyCostReportFootnote,
  RAMP_FUNDING_NEED_FOOTNOTE,
  RAMP_FUNDING_NEED_LABEL,
  Y1_OCCUPANCY_COST_LABEL,
  Y1_EBITDA_LABEL,
  ebitdaNegativeYearsCountFootnote,
  PRE_FINANCE_TDC_FOOTNOTE,
  developerProfitFootnote,
  developerYieldOnCostDetailFootnote,
  developerNoiOnEquityReportFootnote,
  netRealisationFootnote,
  matchedHorizonLabel,
  developerPrimaryUngearedFootnote,
  developerGearedReferenceFootnote,
  DEVELOPER_BOARD_YIELD_ON_GROSS_TDC_FOOTNOTE,
  STABILISED_YEAR1_NOI_FOOTNOTE,
  PROFIT_MARGIN_ON_COST_LABEL,
  developerDecisionPillLabel,
  INPUT_DEBT_CAP_LABEL,
  CONSTRUCTION_DEBT_CAP_INPUT_FOOTNOTE,
  GROSS_DEBT_CAPACITY_LABEL,
  ACTUAL_CONSTRUCTION_DEBT_LABEL,
  grossDebtCapacityFootnote,
  actualConstructionDebtFootnote,
  EQUITY_REQUIRED_LABEL,
  PEAK_CONSTRUCTION_DEBT_LABEL,
  DEVELOPER_EQUITY_LABEL,
  DEVELOPER_EQUITY_REQUIRED_FOOTNOTE,
  contributionTimingFootnote,
  matchedUngearedReturnLabel,
  matchedGearedReferenceLabel,
  NOI_ON_DEVELOPER_EQUITY_LABEL,
  INSPIRED_REDDAM_CONTRIBUTION_LABEL,
  BEFORE_INSPIRED_REDDAM_CONTRIBUTION_FOOTNOTE,
  TOTAL_FUNDING_SOURCES_LABEL,
  SOURCES_LESS_USES_DIFFERENCE_LABEL,
  STABILISED_YEAR1_NOI_LABEL,
  GROSS_TOTAL_DEVELOPMENT_COST_LABEL,
  GROSS_TDC_LABEL,
  YIELD_ON_COST_LABEL,
  EXIT_CAP_RATE_LABEL,
  DEVELOPMENT_SPREAD_LABEL,
  SENSITIVITY_DEBT_STACK_HEADER,
  SENSITIVITY_CONSTRUCTION_FINANCE_HEADER,
  RENT_PCT_REVENUE_SENSITIVITY_HEADER,
  UNGEARED_GEARED_RETURN_SENSITIVITY_HEADER,
  OPERATOR_NPV_LABEL,
  OPERATOR_IRR_LABEL,
  OPERATOR_PAYBACK_LABEL,
  PRE_FINANCE_TDC_LABEL,
} from "../../presentation/return-labels";
import { monteCarloScenarioMatches } from "../../monte-carlo-fingerprint";

const EXPORT_COOLDOWN_MS = 10_000;
const SCOPE_CLASSIFICATION_LABELS = {
  embedded: "Embedded",
  separately_added: "Separately added",
  excluded_tbc: "Excluded / TBC",
  not_applicable: "Not applicable",
} as const;
const exactCurrency = (value: number) =>
  value.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function developerDecisionPill(
  decision: ReturnType<typeof getDeveloperDecision>,
): { className: "success" | "warn" | "danger" | "info"; label: string } {
  const className: "success" | "warn" | "danger" | "info" = !decision.available
    ? "info"
    : decision.status === "met"
      ? "success"
      : decision.status === "marginal"
        ? "warn"
        : "danger";
  return {
    className,
    label: developerDecisionPillLabel(decision.status, decision.available),
  };
}

// ---------------- Report ----------------
export function Report() {
  const { active, result, scenarios, results, mcResult, mcHash } =
    useScenario();
  const a = active.assumptions;
  const k = result.kpis;
  const d = result.developer;
  const developerDecision = getDeveloperDecision(active, result);
  const developerPill = developerDecisionPill(developerDecision);
  const operatorRecommendation = getOperatorDealRecommendation(active, result);
  const exitYear = a.developerExitYear ?? a.modelYears;
  const isEarlyExit = exitYear < a.modelYears;
  const primaryGearedMetric = isEarlyExit
    ? "developerGearedEarlyExitIrr"
    : "developerGearedIrr";
  const horizonLabel = matchedHorizonLabel(exitYear, a.modelYears);
  const rentMethod = a.rentEscalationMethod ?? "fixed";
  const rentEscalationLabel =
    rentMethod === "cpi"
      ? "Rent escalation (CPI)"
      : rentMethod === "market"
        ? "Rent escalation (market)"
        : "Rent escalation (fixed)";
  const monthlyResult = useMemo(
    () => computeMonthly(active, result),
    [active, result],
  );
  const boardCost = useMemo(
    () => buildBoardCostViewModel(active, result),
    [active, result],
  );
  const checks = useMemo(
    () => Model.modelChecks(active, result),
    [active, result],
  );
  const tornadoRows = useMemo(
    () =>
      Model.tornado(active, primaryGearedMetric)
        .filter((r) => Math.abs(r.high - r.low) > 1e-6)
        .slice(0, 10),
    [active, primaryGearedMetric],
  );
  const sensitivityMatrix = useMemo(() => {
    const baseMargin = active.assumptions.constructionMarginRate;
    return [-0.005, 0, 0.005].map((delta) => {
      const s = JSON.parse(JSON.stringify(active)) as typeof active;
      s.assumptions.constructionMarginRate = Math.max(0, baseMargin + delta);
      // computeCore skips the break-even bisection solvers (~121× cheaper than compute())
      // — this matrix only needs developer KPIs which are all in the core result.
      const r = Model.computeCore(s);
      return {
        label:
          delta === 0
            ? "Base"
            : delta > 0
              ? `+${Fmt.pct(Math.abs(delta), 2)}`
              : `-${Fmt.pct(Math.abs(delta), 2)}`,
        margin: s.assumptions.constructionMarginRate,
        profit: r.developer.profit,
        irr: r.kpis.developerPrimaryIrr,
        hurdleMet: r.kpis.developerPrimaryHurdleMet,
      };
    });
  }, [active]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const lastPdfExport = useRef<number>(0);
  const lastExcelExport = useRef<number>(0);
  const [includeMc, setIncludeMc] = useState(false);
  const toast = useToast();
  const auth = useAuthSafe();

  // Keep pdfmake out of the report-screen chunk; preload it only after this route mounts.
  useEffect(() => {
    void import("../../export-pdf")
      .then(({ preloadPdfmake }) => preloadPdfmake())
      .catch((error: unknown) => {
        console.warn(
          "[pdf] Preload unavailable; export will retry on demand.",
          error,
        );
      });
  }, []);
  // No auth context (should not happen in production — AuthProvider wraps the app)
  // fails CLOSED: an entitlement must never widen on an impossible state.
  const excelAllowed = canExportExcel({
    email: auth?.email,
    passcodeMode: auth?.usePasscodeFallback ?? false,
  });
  const mcCheckboxId = useId().replace(/:/g, "-");
  const [reportTornadoRef, reportTornadoWidth] =
    useResizeObserver<HTMLDivElement>(1100);
  const mcStale =
    includeMc &&
    mcResult !== null &&
    !monteCarloScenarioMatches(mcHash, active);
  const excelWorker = useExcelWorker();

  const handleExportPdf = async () => {
    if (pdfLoading) return;
    if (mcStale) {
      toast.warning(
        "Monte Carlo results are stale. Please re-run the simulation before including them in the PDF.",
      );
      return;
    }
    const now = Date.now();
    const remaining = EXPORT_COOLDOWN_MS - (now - lastPdfExport.current);
    if (remaining > 0) {
      toast.warning(
        `PDF export cooling down — please wait ${Math.ceil(remaining / 1000)} s`,
      );
      return;
    }
    setPdfLoading(true);
    try {
      const { exportPdf } = await import("../../export-pdf");
      await exportPdf(
        active,
        result,
        scenarios,
        results,
        monthlyResult,
        includeMc && mcResult ? mcResult : undefined,
      );
      lastPdfExport.current = now;
      toast.success("PDF downloaded successfully");
    } catch (e) {
      console.error("PDF export failed:", e);
      toast.error("PDF export failed. Please try again or use Print.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="page fade-in">
      <div className="page-strip no-print">
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}>
            Board / IC pack
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 28,
              fontWeight: 600,
            }}
          >
            Report &amp; export
          </h2>
          <div className="lede" style={{ marginTop: 6 }}>
            Board-grade PDF with cover page, table of contents, and embedded
            charts.
          </div>
        </div>
        <div className="col" style={{ gap: 8, alignItems: "flex-end" }}>
          <div className="row gap-2">
            <button
              type="button"
              className="btn primary"
              data-testid="download-pdf-btn"
              onClick={handleExportPdf}
              disabled={mcStale || pdfLoading}
              title={
                mcStale
                  ? "Monte Carlo results are stale — re-run simulation first"
                  : "Download board-grade PDF"
              }
              aria-busy={pdfLoading}
            >
              {pdfLoading ? (
                <>
                  <span
                    className="spinner"
                    style={{
                      width: 14,
                      height: 14,
                      border: "2px solid hsl(var(--c-primary-fg) / 0.3)",
                      borderTopColor: "hsl(var(--c-primary-fg))",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                      display: "inline-block",
                      marginRight: 6,
                      verticalAlign: "middle",
                    }}
                  />{" "}
                  Building PDF…
                </>
              ) : (
                <>
                  <Icons.Print size={13} stroke={2} /> Download PDF
                </>
              )}
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={printBusy}
              onClick={async () => {
                setPrintBusy(true);
                try {
                  const { printPdf } = await import("../../export-pdf");
                  await printPdf(
                    active,
                    result,
                    scenarios,
                    results,
                    mcResult || undefined,
                  );
                } catch (e) {
                  console.error("Print failed:", e);
                  globalThis.print();
                } finally {
                  setPrintBusy(false);
                }
              }}
              title={printBusy ? "Preparing print…" : "Browser print (quick)"}
              aria-busy={printBusy}
            >
              <Icons.Print size={13} stroke={2} />{" "}
              {printBusy ? "Printing…" : "Print"}
            </button>
            {excelAllowed && (
              <button
                type="button"
                className="btn primary"
                data-testid="export-excel-btn"
                onClick={async () => {
                  if (excelLoading) return;
                  const now = Date.now();
                  const remaining =
                    EXPORT_COOLDOWN_MS - (now - lastExcelExport.current);
                  if (remaining > 0) {
                    toast.warning(
                      `Excel export cooling down — please wait ${Math.ceil(remaining / 1000)} s`,
                    );
                    return;
                  }
                  setExcelLoading(true);
                  // Yield for paint so the disabled button + spinner render
                  await new Promise((r) => setTimeout(r, 16));
                  try {
                    excelWorker.run(
                      active,
                      result,
                      scenarios,
                      results,
                      async (buffer) => {
                        const projectName = active.assumptions.projectName;
                        const wb = new Workbook();
                        await wb.xlsx.load(buffer);
                        try {
                          await buildChartsSheet(wb, result, active);
                        } catch (error) {
                          throw new Error(
                            `chart assembly: ${error instanceof Error ? error.message : String(error)}`,
                          );
                        }
                        try {
                          applyBoardWorkbookLayout(wb);
                        } catch (error) {
                          throw new Error(
                            `Board consolidation: ${error instanceof Error ? error.message : String(error)}`,
                          );
                        }
                        try {
                          reorderWorksheets(wb, SHEET_ORDER);
                          applyPageSetup(wb, active);
                          normalizeFonts(wb);
                        } catch (error) {
                          throw new Error(
                            `workbook formatting: ${error instanceof Error ? error.message : String(error)}`,
                          );
                        }
                        const finalBuf = await wb.xlsx.writeBuffer();
                        const blob = new Blob([finalBuf], {
                          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        });
                        const url = URL.createObjectURL(blob);
                        const anchor = document.createElement("a");
                        anchor.href = url;
                        const safeName =
                          `${projectName}_${active.name}_model`.replace(
                            /[^a-zA-Z0-9_-]/g,
                            "_",
                          );
                        anchor.download = `${safeName}.xlsx`;
                        document.body.appendChild(anchor);
                        anchor.click();
                        document.body.removeChild(anchor);
                        URL.revokeObjectURL(url);
                        lastExcelExport.current = now;
                        setExcelLoading(false);
                        toast.success("Excel workbook downloaded");
                      },
                      (message) => {
                        console.error("Excel export failed:", message);
                        toast.error("Excel export failed. Please try again.");
                        setExcelLoading(false);
                      },
                    );
                  } catch (e) {
                    console.error("Excel export failed:", e);
                    toast.error("Excel export failed. Please try again.");
                    setExcelLoading(false);
                  }
                }}
                disabled={excelWorker.running}
                title="Export full model to Excel"
                aria-busy={excelWorker.running}
              >
                {excelWorker.running ? (
                  <>
                    <span
                      className="spinner"
                      style={{
                        width: 14,
                        height: 14,
                        border: "2px solid hsl(var(--c-primary-fg) / 0.3)",
                        borderTopColor: "hsl(var(--c-primary-fg))",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        display: "inline-block",
                        marginRight: 6,
                        verticalAlign: "middle",
                      }}
                    />{" "}
                    Building workbook…
                  </>
                ) : (
                  <>
                    <Icons.Download size={13} stroke={2} /> Export Excel
                  </>
                )}
              </button>
            )}
          </div>
          {mcResult && (
            <label
              htmlFor={mcCheckboxId}
              className="row gap-2"
              style={{
                cursor: "pointer",
                fontSize: 13,
                color: "hsl(var(--c-foreground-muted))",
              }}
            >
              <input
                id={mcCheckboxId}
                type="checkbox"
                checked={includeMc}
                onChange={(e) => setIncludeMc(e.target.checked)}
              />
              Include Monte Carlo analysis in PDF
              {mcStale && (
                <span className="pill warn" style={{ fontSize: 11 }}>
                  Stale — re-run required
                </span>
              )}
            </label>
          )}
        </div>
      </div>

      {/* Cover */}
      <div className="hero" style={{ padding: 48 }}>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 16 }}>
            Investment Committee ·{" "}
            {new Date().toLocaleDateString("en-AU", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 44,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              margin: 0,
              lineHeight: 1.1,
              color: "hsl(var(--c-foreground))",
            }}
          >
            {a.projectName}
          </h1>
          <p className="p-lead" style={{ marginTop: 14, maxWidth: "70ch" }}>
            A {a.leaseTermYears}-year campus lease feasibility. Operator:{" "}
            <strong style={{ color: "hsl(var(--c-foreground))" }}>
              {a.operatorName}
            </strong>
            . Site:{" "}
            <strong style={{ color: "hsl(var(--c-foreground))" }}>
              {a.address}
            </strong>
            .
          </p>
          <div className="row gap-2" style={{ marginTop: 20 }}>
            <span className="pill brand">
              <span>Case · {active.name}</span>
            </span>
            <span className={`pill ${developerPill.className}`}>
              <span>{developerPill.label}</span>
            </span>
            <span className={`pill ${k.reddamNpv > 0 ? "success" : "danger"}`}>
              <span>NPV {Fmt.compactCurrency(k.reddamNpv)}</span>
            </span>
            <span className="pill">
              <span>Discount {Fmt.pct(a.discountRate, 1)}</span>
            </span>
          </div>
        </div>
      </div>

      <Section
        className="report-section"
        eyebrow="01"
        title="Assessment"
        lede="Operator assessment first; developer economics are a separate counterparty reasonableness check. The model informs, but does not make, the Board's decision."
      >
        <div className="card glow card-pad-lg">
          {(() => {
            const verdict = operatorRecommendation.verdict;
            const verdictColor =
              operatorRecommendation.tone === "bad"
                ? "hsl(var(--c-destructive))"
                : operatorRecommendation.tone === "warn"
                  ? "hsl(var(--c-warning))"
                  : "hsl(var(--c-success))";
            const transactionResponse = getOperatorBoardDecisionContext(
              operatorRecommendation,
              result,
            );
            const requiredPipeline = Math.max(
              1,
              Math.round((result.years[0]?.enrolment ?? 0) * 1.2),
            );

            return (
              <div className="col" style={{ gap: 18 }}>
                <div className="grid g-3">
                  <div>
                    <div className="eyebrow">Operator assessment</div>
                    <div
                      className="stat-display"
                      style={{
                        color: verdictColor,
                        fontSize: 28,
                        marginTop: 8,
                      }}
                    >
                      {verdict}
                    </div>
                    <p className="p-small">
                      {operatorRecommendation.reason.replace(
                        "DSCR covenant status",
                        "developer financing coverage",
                      )}
                      .
                    </p>
                  </div>
                  <div>
                    <div className="eyebrow">Why this matters</div>
                    <p className="p-small">
                      {Y1_OCCUPANCY_COST_LABEL} is{" "}
                      <strong>
                        {Fmt.pct(k.rentPctRevenueYear1, 0)} of revenue
                      </strong>
                      ; {Y1_EBITDA_LABEL} is{" "}
                      <strong>{Fmt.compactCurrency(k.yearOneEbitda)}</strong>.
                      {rampOpeningLiquidityProseLead()}{" "}
                      <strong>
                        {Fmt.compactCurrency(k.reservesRequiredToSurvive)}
                      </strong>{" "}
                      {RAMP_OPENING_LIQUIDITY_SUPPORT_SUFFIX}. Operator NPV is{" "}
                      <strong>{Fmt.compactCurrency(k.reddamNpv)}</strong> at{" "}
                      {Fmt.pct(a.discountRate, 1)}, with payback{" "}
                      {operatorPaybackProseClause(k.paybackYear)}.
                    </p>
                  </div>
                  <div>
                    <div className="eyebrow">Board decision input</div>
                    <p className="p-small">{transactionResponse}</p>
                  </div>
                </div>
                <div
                  style={{
                    borderTop: "1px solid hsl(var(--c-border-subtle))",
                    paddingTop: 14,
                  }}
                >
                  <div className="eyebrow">Conditions precedent</div>
                  <p className="p-small" style={{ marginBottom: 0 }}>
                    Development approval by Q3 2026; enrolment pipeline of at
                    least {Fmt.num(requiredPipeline, 0)} students six months
                    before opening; fixed-price construction contract; and
                    committed developer debt and equity funding.
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      </Section>

      <Section
        className="report-section"
        eyebrow="02"
        title="Headline economics"
        lede={`As of the ${active.name} scenario.`}
      >
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          Operator economics
        </div>
        <div className="grid g-4" style={{ marginBottom: 22 }}>
          <KPICard
            eyebrow={Y1_OCCUPANCY_COST_LABEL}
            value={Fmt.pct(k.rentPctRevenueYear1, 0)}
            status={
              k.rentPctRevenueYear1 > Model.RENT_PCT_REVENUE_THRESHOLD
                ? "bad"
                : k.rentPctRevenueYear1 > Model.RENT_PCT_REVENUE_CAUTION
                  ? "warn"
                  : "good"
            }
            foot={y1OccupancyCostReportFootnote(
              Fmt.pct(k.rentPctRevenueAvg, 0),
            )}
          />
          <KPICard
            eyebrow={Y1_EBITDA_LABEL}
            value={Fmt.compactCurrency(k.yearOneEbitda)}
            status={k.yearOneEbitda >= 0 ? "good" : "bad"}
            foot={ebitdaNegativeYearsCountFootnote(k.negativeEbitdaYears)}
          />
          <KPICard
            eyebrow={RAMP_FUNDING_NEED_LABEL}
            value={Fmt.compactCurrency(k.reservesRequiredToSurvive)}
            status={k.reservesRequiredToSurvive > 0 ? "bad" : "good"}
            foot={RAMP_FUNDING_NEED_FOOTNOTE}
          />
          <KPICard
            eyebrow={OPERATOR_NPV_LABEL}
            value={Fmt.compactCurrency(k.reddamNpv)}
            status={k.reddamNpv > 0 ? "good" : "bad"}
            foot={operatorNpvDiscountFootnote(Fmt.pct(a.discountRate, 1))}
          />
          <KPICard
            eyebrow={OPERATOR_IRR_LABEL}
            value={Fmt.operatorIrr(k.reddamIrr, 1)}
            foot={
              k.reddamIrr == null
                ? Fmt.OPERATOR_IRR_NA_REASON
                : OPERATOR_IRR_CONVERGED_FOOTNOTE
            }
          />
          <KPICard
            eyebrow={OPERATOR_PAYBACK_LABEL}
            value={Fmt.payback(k.paybackYear)}
            foot={operatorPaybackFootnote(
              Fmt.compactCurrency(-result.campus.totalReddamCapex),
            )}
          />
          <KPICard
            eyebrow="Steady-state revenue"
            value={Fmt.compactCurrency(k.steadyStateRevenue)}
            foot={modelHorizonYearFootnote(a.modelYears)}
          />
          <KPICard
            eyebrow="Total tax (term)"
            value={Fmt.compactCurrency(k.totalTax)}
            foot={operatorTotalTaxFootnote(
              Fmt.pct(a.corporateTaxRate, 0),
              a.modelYears,
            )}
          />
        </div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          Developer counterparty diagnostics
        </div>
        <div className="grid g-4">
          <KPICard
            eyebrow="Matched ungeared achieved return"
            value={Fmt.pct(k.developerPrimaryIrr, 1)}
            status={
              developerDecision.status === "met"
                ? "good"
                : developerDecision.status === "marginal"
                  ? "warn"
                  : "bad"
            }
            foot={developerPrimaryUngearedFootnote(
              Fmt.pct(a.developerHurdleIRR, 1),
              matchedHorizonLabel(exitYear, a.modelYears),
            )}
          />
          <KPICard
            eyebrow="Matched geared achieved return"
            value={
              Number.isFinite(k.developerPrimaryGearedIrr)
                ? Fmt.pct(k.developerPrimaryGearedIrr, 1)
                : "—"
            }
            foot={developerGearedReferenceFootnote(
              d.gearedHurdleConverged && Number.isFinite(d.gearedHurdle)
                ? Fmt.pct(d.gearedHurdle, 1)
                : null,
              Fmt.compactCurrency(d.equityRequired),
            )}
          />
          <KPICard
            eyebrow={gearedRefinanceExitLabel(a.termLoanHoldYears)}
            value={
              Number.isFinite(d.gearedWholeOfLifeIrr)
                ? Fmt.pct(d.gearedWholeOfLifeIrr, 1)
                : "—"
            }
            foot={gearedRefinanceExitFootnote(
              a.termLoanHoldYears,
              Fmt.pct(a.termLoanLvrRate, 0),
            )}
          />
          <KPICard
            eyebrow={NOI_ON_DEVELOPER_EQUITY_LABEL}
            value={Fmt.pct(d.yieldOnCash, 2)}
            foot={developerNoiOnEquityReportFootnote(
              Fmt.compactCurrency(d.equityRequired),
            )}
          />
          <KPICard
            eyebrow={EQUITY_REQUIRED_LABEL}
            value={Fmt.compactCurrency(d.equityRequired)}
            foot={developerEquityRequiredLvrFootnote(
              Fmt.pct(1 - a.constructionLvrRate, 0),
              Fmt.pct(a.constructionLvrRate, 0),
            )}
          />
          <KPICard
            eyebrow={YIELD_ON_COST_LABEL}
            value={Fmt.pct(d.yieldOnCost, 2)}
            foot={developerYieldOnCostDetailFootnote(
              Fmt.compactCurrency(d.noi),
            )}
          />
          <KPICard
            eyebrow={PRE_FINANCE_TDC_LABEL}
            value={Fmt.compactCurrency(d.totalCost)}
            foot={PRE_FINANCE_TDC_FOOTNOTE}
          />
          <KPICard
            eyebrow="Net realisation"
            value={Fmt.compactCurrency(d.netRealisation)}
            foot={netRealisationFootnote(
              exitYear,
              a.modelYears,
              Fmt.pct(a.capRate, 2),
              Fmt.compactCurrency(d.noi),
            )}
          />
          <KPICard
            eyebrow="Profit"
            value={Fmt.compactCurrency(d.profit)}
            status={d.profit > 0 ? "good" : "bad"}
            foot={developerProfitFootnote(Fmt.pct(k.developerMarginOnCost, 1))}
          />
        </div>
        <p className="p-small" style={{ marginTop: 12 }}>
          {GEARED_REFERENCE_DISCLOSURE}
        </p>
      </Section>

      <Section
        className="report-section"
        eyebrow="03"
        title="Development cost & yield"
        lede="Gross development cost, funding sources, cost confidence and the developer-return diagnostics used to test the counterparty position."
      >
        <div className="grid g-4">
          <KPICard
            eyebrow={GROSS_TOTAL_DEVELOPMENT_COST_LABEL}
            value={Fmt.compactCurrency(boardCost.headline.grossTdc)}
            foot={BEFORE_INSPIRED_REDDAM_CONTRIBUTION_FOOTNOTE}
          />
          <KPICard
            eyebrow={STABILISED_YEAR1_NOI_LABEL}
            value={Fmt.compactCurrency(boardCost.headline.stabilisedYear1Noi)}
            foot={STABILISED_YEAR1_NOI_FOOTNOTE}
          />
          <KPICard
            eyebrow={YIELD_ON_COST_LABEL}
            value={Fmt.pct(boardCost.headline.yieldOnCost, 2)}
            foot={DEVELOPER_BOARD_YIELD_ON_GROSS_TDC_FOOTNOTE}
          />
          <KPICard
            eyebrow="Cost basis confidence"
            value={boardCost.evidence.confidence}
            status={
              boardCost.evidence.confidence === "reconciled"
                ? "good"
                : boardCost.evidence.confidence === "incomplete"
                  ? "bad"
                  : "warn"
            }
            foot={`${boardCost.evidence.counts.included} included · ${boardCost.evidence.counts.provisional} provisional · ${boardCost.evidence.counts.missing} missing`}
          />
        </div>

        {boardCost.sourceBasis && boardCost.constructionBridge && (
          <>
            <div
              className="grid g-2 board-funding-grid"
              style={{ marginTop: 16 }}
            >
              <div className="card card-pad-lg">
                <div className="eyebrow">Adopted source cost</div>
                <p>
                  <strong>{boardCost.sourceBasis.sourceName}</strong> ·{" "}
                  {boardCost.sourceBasis.sourceDate}
                </p>
                <KVCard
                  title="Loaded construction basis"
                  rows={[
                    [
                      "Building works",
                      Fmt.compactCurrency(boardCost.sourceBasis.buildingWorks),
                    ],
                    [
                      "Site / outdoor works",
                      Fmt.compactCurrency(boardCost.sourceBasis.siteWorks),
                    ],
                    [
                      "Adopted construction",
                      Fmt.compactCurrency(
                        boardCost.sourceBasis.adoptedConstruction,
                      ),
                    ],
                    [
                      "Source / scenario building area",
                      `${boardCost.sourceBasis.sourceBuildingAreaSqm.toLocaleString("en-AU")} / ${boardCost.sourceBasis.scenarioBuildingAreaSqm.toLocaleString("en-AU")} m²`,
                    ],
                    [
                      "GST / escalation",
                      `${boardCost.sourceBasis.gstStatus} / ${boardCost.sourceBasis.escalationStatus}`,
                    ],
                  ]}
                />
                <p className="p-small">
                  The fixed source total is preserved. Scenario GFA is used only
                  for provisional phasing allocation.
                </p>
              </div>
              <div className="card card-pad-lg">
                <div className="eyebrow">Embedded versus separate</div>
                <ul className="p-small">
                  {boardCost.sourceBasis.embeddedLoadings.map((loading) => (
                    <li key={loading.id}>
                      {loading.label}: {Fmt.pct(loading.rate, 1)} embedded — not
                      added again
                    </li>
                  ))}
                </ul>
                <p className="p-small">
                  Contingency, professional fees, development fee, consents and
                  finance are separately identified. GST remains unverified.
                </p>
              </div>
            </div>

            <div className="card elevated" style={{ marginTop: 16 }}>
              <div className="table-scroll">
                <table className="table" aria-label="Construction cost bridge">
                  <thead>
                    <tr>
                      <th scope="col">Construction bridge</th>
                      <th scope="col">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">Adopted construction cost</th>
                      <td style={{ textAlign: "right" }}>
                        {exactCurrency(
                          boardCost.constructionBridge.adoptedTotal,
                        )}
                      </td>
                    </tr>
                    {boardCost.constructionBridge.rows.map((row) => (
                      <tr key={row.id}>
                        <th scope="row">{row.label}</th>
                        <td style={{ textAlign: "right" }}>
                          {exactCurrency(row.amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="row-emph">
                      <th scope="row">Upper-bound comparator</th>
                      <td style={{ textAlign: "right" }}>
                        {exactCurrency(
                          boardCost.constructionBridge.upperBoundTotal,
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <div
          className="card elevated board-cost-schedule-card"
          style={{ marginTop: 16 }}
        >
          <div className="table-scroll">
            <table
              className="table board-cost-schedule"
              aria-label="Development cost schedule"
            >
              <thead>
                <tr>
                  <th scope="col">Scope / building</th>
                  <th scope="col">Area</th>
                  <th scope="col">Base cost</th>
                  <th scope="col">Loading basis</th>
                  <th scope="col">Scope state</th>
                  <th scope="col">Source / as at</th>
                </tr>
              </thead>
              <tbody>
                {boardCost.costSchedule.map((row) => {
                  return (
                    <tr key={row.id}>
                      <th scope="row">{row.label}</th>
                      <td style={{ textAlign: "right" }}>
                        {row.areaSqm == null ? (
                          <span
                            aria-label={`Not available — ${BOARD_COST_STATUS_LABELS[row.status]}`}
                          >
                            —
                          </span>
                        ) : (
                          `${Math.round(row.areaSqm).toLocaleString("en-AU")} m²`
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {row.amount == null ? (
                          <span
                            aria-label={`Not available — ${BOARD_COST_STATUS_LABELS[row.status]}`}
                          >
                            —
                          </span>
                        ) : (
                          Fmt.compactCurrency(row.amount)
                        )}
                      </td>
                      <td>{BOARD_COST_BASIS_LABELS[row.basis]}</td>
                      <td>
                        <span className="pill info">
                          {BOARD_COST_STATUS_LABELS[row.status]}
                        </span>
                      </td>
                      <td>
                        {row.source ?? "Source not available"}
                        {row.asAt ? ` · ${row.asAt}` : ""}
                      </td>
                    </tr>
                  );
                })}
                <tr className="row-emph">
                  <th scope="row">{GROSS_TOTAL_DEVELOPMENT_COST_LABEL}</th>
                  <td colSpan={4}>
                    Model total — not the sum of comparison evidence rows
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {Fmt.compactCurrency(boardCost.costScheduleTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="p-small" style={{ margin: 16 }}>
            {boardCost.evidence.message}
          </p>
        </div>

        <div className="grid g-2 board-funding-grid" style={{ marginTop: 16 }}>
          <div className="card card-pad-lg">
            <div className="eyebrow">Uses</div>
            <strong>{GROSS_TDC_LABEL}</strong>
            <div className="stat-display">
              {Fmt.compactCurrency(boardCost.funding.grossTdc)}
            </div>
          </div>
          <div className="card card-pad-lg">
            <div className="eyebrow">Funding sources</div>
            <p>
              {INSPIRED_REDDAM_CONTRIBUTION_LABEL}:{" "}
              <strong>
                {Fmt.compactCurrency(boardCost.funding.contribution)}
              </strong>
            </p>
            <p className="p-small">{OPERATOR_CONTRIBUTION_BOARD_NOTE}</p>
            <p>
              {INPUT_DEBT_CAP_LABEL}:{" "}
              <strong>{Fmt.pct(a.constructionLvrRate ?? 0.7, 0)}</strong>
            </p>
            <p className="p-small">{CONSTRUCTION_DEBT_CAP_INPUT_FOOTNOTE}</p>
            <p>
              {GROSS_DEBT_CAPACITY_LABEL}:{" "}
              <strong>
                {Fmt.compactCurrency(boardCost.funding.grossDebtCapacity)}
              </strong>
            </p>
            <p className="p-small">
              {grossDebtCapacityFootnote(
                Fmt.pct(d.grossFacilityLimitDev / Math.max(1, d.totalCost), 1),
              )}
            </p>
            <p>
              {ACTUAL_CONSTRUCTION_DEBT_LABEL}:{" "}
              <strong>
                {Fmt.compactCurrency(boardCost.funding.constructionDebt)}
              </strong>
            </p>
            <p className="p-small">
              {actualConstructionDebtFootnote(
                Fmt.pct(d.facilityLimitDev / Math.max(1, d.totalCost), 1),
              )}
            </p>
            <p>
              {PEAK_CONSTRUCTION_DEBT_LABEL}:{" "}
              <strong>{Fmt.compactCurrency(boardCost.funding.peakDebt)}</strong>
            </p>
            <p className="p-small">
              {contributionTimingFootnote(
                a.contributionTiming ?? "progressive",
              )}
            </p>
            <p>
              {DEVELOPER_EQUITY_LABEL}:{" "}
              <strong>
                {Fmt.compactCurrency(boardCost.funding.developerEquity)}
              </strong>
            </p>
            <p className="p-small">{DEVELOPER_EQUITY_REQUIRED_FOOTNOTE}</p>
            <p>
              {TOTAL_FUNDING_SOURCES_LABEL}:{" "}
              <strong>
                {Fmt.compactCurrency(boardCost.funding.totalSources)}
              </strong>
            </p>
            {Math.abs(boardCost.funding.difference) > 0.01 && (
              <p role="alert">
                {SOURCES_LESS_USES_DIFFERENCE_LABEL}:{" "}
                {Fmt.preciseCurrency(boardCost.funding.difference)}
              </p>
            )}
          </div>
        </div>

        {boardCost.scopeRegister.length > 0 && (
          <div className="card elevated" style={{ marginTop: 16 }}>
            <div className="table-scroll">
              <table className="table" aria-label="Scope double-count register">
                <thead>
                  <tr>
                    <th scope="col">Scope</th>
                    <th scope="col">Classification</th>
                    <th scope="col">Model mapping</th>
                    <th scope="col">Disclosure</th>
                  </tr>
                </thead>
                <tbody>
                  {boardCost.scopeRegister.map((item) => (
                    <tr key={item.id}>
                      <th scope="row">{item.label}</th>
                      <td>
                        {SCOPE_CLASSIFICATION_LABELS[item.classification]}
                      </td>
                      <td>{item.mapping}</td>
                      <td>{item.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {boardCost.sensitivities.length > 0 && (
          <div className="card elevated" style={{ marginTop: 16 }}>
            <div className="table-scroll">
              <table
                className="table"
                aria-label="Fixed cost and contribution sensitivities"
              >
                <thead>
                  <tr>
                    <th scope="col">Case</th>
                    <th scope="col">Rent Y1</th>
                    <th scope="col">{RENT_PCT_REVENUE_SENSITIVITY_HEADER}</th>
                    <th scope="col">{OPERATOR_NPV_LABEL}</th>
                    <th scope="col">
                      {UNGEARED_GEARED_RETURN_SENSITIVITY_HEADER}
                    </th>
                    <th scope="col">{YIELD_ON_COST_LABEL}</th>
                    <th scope="col">{DEVELOPER_EQUITY_LABEL}</th>
                    <th scope="col">{SENSITIVITY_DEBT_STACK_HEADER}</th>
                    <th scope="col">
                      {SENSITIVITY_CONSTRUCTION_FINANCE_HEADER}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {boardCost.sensitivities.map((item) => (
                    <tr key={item.id}>
                      <th scope="row">{item.label}</th>
                      <td>{Fmt.compactCurrency(item.rentYear1)}</td>
                      <td>{Fmt.pct(item.rentPctRevenueYear1, 1)}</td>
                      <td>{Fmt.compactCurrency(item.operatorNpv)}</td>
                      <td>
                        {Fmt.pct(item.developerUngearedReturn, 1)} /{" "}
                        {Fmt.pct(item.developerGearedReturn, 1)}
                      </td>
                      <td>{Fmt.pct(item.yieldOnCost, 2)}</td>
                      <td>{Fmt.compactCurrency(item.developerEquity)}</td>
                      <td>
                        {Fmt.compactCurrency(item.grossDebtCapacity)} /{" "}
                        {Fmt.compactCurrency(item.actualDebt)} /{" "}
                        {Fmt.compactCurrency(item.peakDebt)}
                      </td>
                      <td>
                        {Fmt.compactCurrency(item.constructionInterest)} /{" "}
                        {Fmt.compactCurrency(item.lineFee)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="p-small" style={{ margin: 16 }}>
              {BOARD_COST_COPY.diagnosticDisclosure} Rent is held at the
              scenario ask in every case.
            </p>
          </div>
        )}

        <div
          className="card glow card-pad-lg board-yield-bridge"
          style={{ marginTop: 16 }}
        >
          <p className="sr-only">{BOARD_COST_COPY.yieldOnCostDefinition}</p>
          <div className="grid board-yield-bridge-values" aria-hidden="true">
            <div>
              <div className="eyebrow">{STABILISED_YEAR1_NOI_LABEL}</div>
              <strong>
                {Fmt.compactCurrency(boardCost.headline.stabilisedYear1Noi)}
              </strong>
            </div>
            <div>
              <div className="eyebrow">{GROSS_TDC_LABEL}</div>
              <strong>
                {Fmt.compactCurrency(boardCost.headline.grossTdc)}
              </strong>
            </div>
            <div>
              <div className="eyebrow">{YIELD_ON_COST_LABEL}</div>
              <strong>{Fmt.pct(boardCost.headline.yieldOnCost, 2)}</strong>
            </div>
            <div>
              <div className="eyebrow">{EXIT_CAP_RATE_LABEL}</div>
              <strong>{Fmt.pct(boardCost.headline.exitCapRate, 2)}</strong>
            </div>
            <div>
              <div className="eyebrow">{DEVELOPMENT_SPREAD_LABEL}</div>
              <strong>
                {Fmt.pct(boardCost.headline.developmentSpread, 2)}
              </strong>
            </div>
          </div>
          <p className="p-small">
            Funding contributions do not change this denominator.
          </p>
        </div>

        <div className="card card-pad-lg" style={{ marginTop: 16 }}>
          <div className="eyebrow">
            Development-cost counterparty diagnostics
          </div>
          <KVCard
            title="Selected-horizon return and funding reference"
            rows={[
              [
                matchedUngearedReturnLabel(boardCost.diagnostics.horizon.label),
                Fmt.pct(boardCost.diagnostics.matchedUngearedReturn, 1),
              ],
              [
                matchedGearedReferenceLabel(
                  boardCost.diagnostics.horizon.label,
                ),
                Fmt.pct(boardCost.diagnostics.matchedGearedReturn, 1),
              ],
              [
                PROFIT_MARGIN_ON_COST_LABEL,
                `${Fmt.compactCurrency(boardCost.diagnostics.profit)} / ${Fmt.pct(boardCost.diagnostics.marginOnCost, 1)}`,
              ],
              [
                ACTUAL_CONSTRUCTION_DEBT_LABEL,
                Fmt.compactCurrency(boardCost.diagnostics.developerDebt),
              ],
              [
                DEVELOPER_EQUITY_LABEL,
                Fmt.compactCurrency(boardCost.diagnostics.developerEquity),
              ],
              [
                boardCost.diagnostics.noiOnDeveloperEquity.label,
                Fmt.pct(boardCost.diagnostics.noiOnDeveloperEquity.value, 2),
              ],
            ]}
          />
          <p className="p-small">
            These metrics test the developer's cost and return position. The
            school-board decision remains governed by affordability, covenant
            safety and downside resilience.
          </p>
        </div>
      </Section>

      <Section
        className="report-section"
        eyebrow="04"
        title="Scenario comparison"
        right={(() => {
          const buildScenarioTsv = () => {
            const header = [
              "Scenario",
              "Matched-horizon ungeared IRR",
              "Stated hurdle",
              "Margin",
              "Profit",
              "Operator EBITDA margin",
              "Rent / student",
              OPERATOR_NPV_LABEL,
              OPERATOR_IRR_LABEL,
              OPERATOR_PAYBACK_LABEL,
            ].join("\t");
            const tsvRows = scenarios.map((s) => {
              const r = results[s.id];
              const hStat = r.kpis.developerPrimaryHurdleStatus;
              const yEnd = r.years[r.years.length - 1];
              const y1 = r.years[0];
              const ebitdaMargin =
                yEnd && yEnd.totalRevenue > 0
                  ? Fmt.pct(yEnd.reddamEbitda / yEnd.totalRevenue, 1)
                  : "—";
              const rentPerStudent =
                y1 && y1.enrolment > 0
                  ? Fmt.compactCurrency(y1.rentGross / y1.enrolment)
                  : "—";
              let hurdleLabel: string;
              switch (hStat) {
                case "met":
                  hurdleLabel = "Yes";
                  break;
                case "marginal":
                  hurdleLabel = "Marginal";
                  break;
                default:
                  hurdleLabel = "No";
                  break;
              }
              return [
                s.name,
                Fmt.pct(r.kpis.developerPrimaryIrr, 1),
                hurdleLabel,
                Fmt.pct(r.kpis.developerMarginOnCost, 1),
                Fmt.compactCurrency(r.developer.profit),
                ebitdaMargin,
                rentPerStudent,
                Fmt.compactCurrency(r.kpis.reddamNpv),
                Fmt.operatorIrr(r.kpis.reddamIrr, 1),
                r.kpis.paybackYear ? `Y${r.kpis.paybackYear}` : "—",
              ].join("\t");
            });
            return [header, ...tsvRows].join("\n");
          };
          return (
            <div className="row gap-2">
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  const tsv = buildScenarioTsv();
                  const blob = new Blob(["\uFEFF" + tsv], {
                    type: "text/tab-separated-values;charset=utf-8",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${active.name}_scenarios.tsv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                title="Download scenario comparison as TSV (opens in Excel)"
              >
                <Icons.Download size={14} stroke={2} /> Excel
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  navigator.clipboard
                    .writeText(buildScenarioTsv())
                    .catch(() => {});
                }}
                title="Copy table to clipboard (tab-separated)"
              >
                <Icons.Copy size={14} stroke={2} /> Copy
              </button>
            </div>
          );
        })()}
      >
        <div className="card elevated">
          <div className="table-scroll">
            <table className="table" aria-label="Scenario comparison">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Matched-horizon ungeared IRR</th>
                  <th>Stated hurdle</th>
                  <th>Margin</th>
                  <th>Profit</th>
                  <th
                    style={{ whiteSpace: "nowrap", minWidth: 110 }}
                    title="Operator EBITDA margin"
                  >
                    EBITDA margin
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>Rent / student</th>
                  <th>{OPERATOR_NPV_LABEL}</th>
                  <th>{OPERATOR_IRR_LABEL}</th>
                  <th>{OPERATOR_PAYBACK_LABEL}</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map((s) => {
                  const r = results[s.id];
                  return (
                    <tr
                      key={s.id}
                      className={`${s.id === active.id ? "row-emph" : ""} row-hover`}
                    >
                      <td className="row-label">
                        {s.name}
                        {s.id === active.id ? " · active" : ""}
                      </td>
                      <td>{Fmt.pct(r.kpis.developerPrimaryIrr, 1)}</td>
                      <td>
                        {r.kpis.developerPrimaryHurdleMet === true ? (
                          <span className="fg-success">✓</span>
                        ) : r.kpis.developerPrimaryHurdleMet === false ? (
                          <span className="fg-warning">✗</span>
                        ) : (
                          <span aria-label="Unavailable">—</span>
                        )}
                      </td>
                      <td>{Fmt.pct(r.kpis.developerMarginOnCost, 1)}</td>
                      <td>{Fmt.compactCurrency(r.developer.profit)}</td>
                      <td>
                        {(() => {
                          const y = r.years[r.years.length - 1];
                          return y && y.totalRevenue > 0
                            ? Fmt.pct(y.reddamEbitda / y.totalRevenue, 1)
                            : "—";
                        })()}
                      </td>
                      <td>
                        {(() => {
                          const y = r.years[0];
                          return y && y.enrolment > 0
                            ? Fmt.compactCurrency(y.rentGross / y.enrolment)
                            : "—";
                        })()}
                      </td>
                      <td
                        style={{
                          color:
                            r.kpis.reddamNpv > 0
                              ? "hsl(var(--c-success))"
                              : "hsl(var(--c-destructive))",
                        }}
                      >
                        {Fmt.compactCurrency(r.kpis.reddamNpv)}
                      </td>
                      <td>{Fmt.operatorIrr(r.kpis.reddamIrr, 1)}</td>
                      <td>{Fmt.payback(r.kpis.paybackYear)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section
        className="report-section"
        eyebrow="05"
        title="Drivers of value"
        lede={`Tornado on ${Fmt.pct(k.developerPrimaryGearedIrr, 1)} matched-horizon geared IRR (${horizonLabel}). Financing is diagnostic context only. Each driver flexed ± its plausible range; bar tips show the resulting IRR at each extreme.`}
      >
        <div ref={reportTornadoRef} className="card elevated card-pad-lg">
          <Tornado
            rows={tornadoRows}
            base={k.developerPrimaryGearedIrr}
            width={Math.max(320, Math.floor(reportTornadoWidth))}
            rowHeight={32}
            valueFormat={(v) => Fmt.pct(v, 1)}
          />
        </div>
      </Section>

      <Section
        className="report-section"
        eyebrow="06"
        title="Sensitivity matrix"
        lede="Construction margin rate vs. developer returns."
      >
        <div className="card elevated">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Margin shift</th>
                  <th>Margin rate</th>
                  <th>Developer profit</th>
                  <th>Matched-horizon ungeared IRR</th>
                  <th>Stated hurdle status</th>
                </tr>
              </thead>
              <tbody>
                {sensitivityMatrix.map((m) => (
                  <tr key={m.label}>
                    <td className="row-label">{m.label}</td>
                    <td>{Fmt.pct(m.margin, 2)}</td>
                    <td>{Fmt.compactCurrency(m.profit)}</td>
                    <td>{Fmt.pct(m.irr, 1)}</td>
                    <td>
                      {m.hurdleMet === true ? (
                        <span className="pill success">PASS</span>
                      ) : m.hurdleMet === false ? (
                        <span className="pill danger">FAIL</span>
                      ) : (
                        <span className="pill">UNAVAILABLE</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section
        className="report-section"
        eyebrow="07"
        title="Per-building economics"
        lede="Granular view of each building's cost, rent, and capacity contribution."
      >
        <div className="card elevated">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Building</th>
                  <th>Online</th>
                  <th>NLA</th>
                  <th>GFA</th>
                  <th>Hard cost</th>
                  <th>$/sqm GFA</th>
                  <th>Y1 net rent</th>
                  <th>Y1 outgoings</th>
                  <th>Net rent / sqm</th>
                  <th>Yield on cost</th>
                  <th>Seats</th>
                </tr>
              </thead>
              <tbody>
                {active.buildings.map((b) => (
                  <tr key={b.id}>
                    <td className="row-label">{b.name}</td>
                    <td>Y{b.comesOnlineYear}</td>
                    <td>{Fmt.sqm(b.nlaSqm)}</td>
                    <td>{Fmt.sqm(b.gfaSqm)}</td>
                    <td>
                      {Fmt.compactCurrency(b.gfaSqm * b.constructionPerSqmGfa)}
                    </td>
                    <td>{Fmt.fullCurrency(b.constructionPerSqmGfa)}</td>
                    <td>{Fmt.compactCurrency(b.nlaSqm * b.netRentPerSqm)}</td>
                    <td>{Fmt.compactCurrency(b.nlaSqm * b.outgoingsPerSqm)}</td>
                    <td>{Fmt.fullCurrency(b.netRentPerSqm)}</td>
                    <td>
                      {Fmt.pct(
                        (b.nlaSqm * b.netRentPerSqm) /
                          Math.max(1, b.gfaSqm * b.constructionPerSqmGfa),
                        1,
                      )}
                    </td>
                    <td>{Fmt.num(b.enrolmentCapacity, 0)}</td>
                  </tr>
                ))}
                <tr className="row-emph">
                  <td>Total / weighted</td>
                  <td></td>
                  <td>{Fmt.sqm(result.campus.totalNlaSqm)}</td>
                  <td>{Fmt.sqm(result.campus.totalGfaSqm)}</td>
                  <td>
                    {Fmt.compactCurrency(result.developer.hardConstruction)}
                  </td>
                  <td>
                    {Fmt.fullCurrency(
                      result.developer.hardConstruction /
                        Math.max(1, result.campus.totalGfaSqm),
                    )}
                  </td>
                  <td>{Fmt.compactCurrency(result.developer.netRentYear1)}</td>
                  <td>
                    {Fmt.compactCurrency(result.campus.totalYear1Outgoings)}
                  </td>
                  <td>
                    {Fmt.fullCurrency(
                      result.developer.netRentYear1 /
                        Math.max(1, result.campus.totalNlaSqm),
                    )}
                  </td>
                  <td>
                    {Fmt.pct(
                      result.developer.netRentYear1 /
                        Math.max(1, result.developer.hardConstruction),
                      1,
                    )}
                  </td>
                  <td>{Fmt.num(result.campus.totalEnrolmentCapacity, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section
        className="report-section"
        eyebrow="08"
        title="Development-period and operator cashflow"
        lede={`Developer construction uses are separated from operator cashflow. Year 1 is monthly; Year 2 onward is annual, with ramp and steady-state stages shown independently.`}
      >
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          Developer development uses
        </div>
        <div className="card elevated" style={{ marginBottom: 20 }}>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Stage</th>
                  <th style={{ textAlign: "right" }}>Developer outflow</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {monthlyResult.months
                  .filter((month) => month.phase === "development")
                  .map((m) => (
                    <tr key={m.month}>
                      <td className="row-label">{m.label}</td>
                      <td>
                        <span className="pill">{m.phase}</span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {m.developerOutflow > 0
                          ? Fmt.compactCurrency(m.developerOutflow)
                          : "—"}
                      </td>
                      <td className="p-micro">{m.notes}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          Operator operating cashflow
        </div>
        <div className="card elevated">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Frequency</th>
                  <th>Stage</th>
                  <th style={{ textAlign: "right" }}>Enrolment</th>
                  <th style={{ textAlign: "right" }}>Operator revenue</th>
                  <th style={{ textAlign: "right" }}>
                    Operator occupancy + opex
                  </th>
                  <th style={{ textAlign: "right" }}>Operator net income</th>
                  <th style={{ textAlign: "right" }}>
                    Operator cumulative cash
                  </th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {monthlyResult.months
                  .filter((month) => month.phase !== "development")
                  .map((m) => (
                    <tr
                      key={m.month}
                      className={m.phase === "ramp" ? "row-emph" : ""}
                    >
                      <td className="row-label">{m.label}</td>
                      <td>
                        {/^Y1 M\d+$/u.test(m.label) ? "Monthly" : "Annual"}
                      </td>
                      <td>
                        <span
                          className={`pill ${m.phase === "ramp" ? "brand" : "success"}`}
                        >
                          {m.phase}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {m.enrolment != null ? Fmt.num(m.enrolment, 0) : "—"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {m.reddamRevenue > 0
                          ? Fmt.compactCurrency(m.reddamRevenue)
                          : "—"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {m.reddamOutflow > 0
                          ? Fmt.compactCurrency(m.reddamOutflow)
                          : "—"}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          color:
                            m.reddamNetIncome >= 0
                              ? "hsl(var(--c-success))"
                              : "hsl(var(--c-destructive))",
                        }}
                      >
                        {Fmt.compactCurrency(m.reddamNetIncome)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {Fmt.compactCurrency(m.cumulativeCashflow)}
                      </td>
                      <td className="p-micro">{m.notes}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="p-small" style={{ marginTop: 10 }}>
          Operator cumulative cash includes opening operator capex of{" "}
          {Fmt.compactCurrency(result.campus.totalReddamCapex)} before the first
          operating period. Developer outflows and operator cash are separate
          ownership lenses.
        </p>
      </Section>

      <Section className="report-section" eyebrow="09" title="Audit summary">
        <div className="grid g-2">
          <div className="card elevated card-pad-lg">
            <div className="eyebrow">Self-checks</div>
            {(() => {
              const pass = checks.filter((c) => c.level === "pass").length;
              const warn = checks.filter((c) => c.level === "warn").length;
              const fail = checks.filter((c) => c.level === "fail").length;
              return (
                <div className="row gap-4" style={{ marginTop: 12 }}>
                  <div>
                    <div className="p-micro">Passed</div>
                    <div
                      className="stat-display"
                      style={{ fontSize: 30, color: "hsl(var(--c-success))" }}
                    >
                      {pass}
                    </div>
                  </div>
                  <div>
                    <div className="p-micro">Watch</div>
                    <div
                      className="stat-display"
                      style={{
                        fontSize: 30,
                        color: warn
                          ? "hsl(var(--c-warning))"
                          : "hsl(var(--c-foreground-muted))",
                      }}
                    >
                      {warn}
                    </div>
                  </div>
                  <div>
                    <div className="p-micro">Failed</div>
                    <div
                      className="stat-display"
                      style={{
                        fontSize: 30,
                        color: fail
                          ? "hsl(var(--c-destructive))"
                          : "hsl(var(--c-foreground-muted))",
                      }}
                    >
                      {fail}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="card elevated card-pad-lg">
            <div className="eyebrow">Flags raised</div>
            {result.flags.length === 0 ? (
              <div style={{ marginTop: 12 }}>
                <span className="pill success">PASS</span> No flags — model
                within all guardrails.
              </div>
            ) : (
              <div className="col" style={{ gap: 6, marginTop: 12 }}>
                {result.flags.map((f, i) => (
                  <FlagItem key={`${f.message}-${i}`} {...f} />
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section
        className="report-section"
        eyebrow="10"
        title="Fee affordability"
      >
        <div className="card elevated card-pad-lg">
          <div className="grid g-3">
            <div>
              <div className="eyebrow">Headline fee</div>
              <div className="stat-display" style={{ fontSize: 28 }}>
                {Fmt.compactCurrency(a.feePerStudentYear1)}
              </div>
              <div className="p-small" style={{ marginTop: 4 }}>
                Y1 blended tuition
              </div>
            </div>
            <div>
              <div className="eyebrow">Effective fee</div>
              <div className="stat-display" style={{ fontSize: 28 }}>
                {Fmt.compactCurrency(result.years[0]?.effectiveFee ?? 0)}
              </div>
              <div className="p-small" style={{ marginTop: 4 }}>
                after {Fmt.pct(a.foundingFamilyDiscount + a.siblingDiscount, 0)}{" "}
                discount (Y1–Y{a.discountDecayYears})
              </div>
            </div>
            <div>
              <div className="eyebrow">vs Median HH income</div>
              <div className="stat-display" style={{ fontSize: 28 }}>
                {(() => {
                  const medianHH =
                    catchmentFor(active).annual_median_household_income ??
                    120_000;
                  return medianHH > 0
                    ? Fmt.pct(
                        (result.years[0]?.effectiveFee ?? 0) / medianHH,
                        1,
                      )
                    : "—";
                })()}
              </div>
              <div className="p-small" style={{ marginTop: 4 }}>
                of{" "}
                {Fmt.compactCurrency(
                  catchmentFor(active).annual_median_household_income ??
                    120_000,
                )}{" "}
                median ({catchmentFor(active).area_name})
              </div>
            </div>
          </div>
          <div className="lede" style={{ marginTop: 16 }}>
            {(() => {
              const medianHH =
                catchmentFor(active).annual_median_household_income ?? 120_000;
              return (
                <>
                  The Y1 blended fee of{" "}
                  {Fmt.compactCurrency(a.feePerStudentYear1)} represents{" "}
                  {medianHH > 0
                    ? Fmt.pct(a.feePerStudentYear1 / medianHH, 1)
                    : "—"}{" "}
                  of {catchmentFor(active).area_name} median household income.
                  With {Fmt.pct(a.foundingFamilyDiscount, 0)} founding-family
                  and {Fmt.pct(a.siblingDiscount, 0)} sibling discounts for the
                  first {a.discountDecayYears} years, the effective fee is{" "}
                  {Fmt.compactCurrency(result.years[0]?.effectiveFee ?? 0)}. At{" "}
                  {Fmt.pct(a.tuitionGrowthPct, 1)} nominal growth, fees outpace
                  assumed CPI of{" "}
                  {Fmt.pct(a.cpiAssumption ?? DEFAULT_CPI_ASSUMPTION, 1)} by{" "}
                  {Fmt.pct(
                    a.tuitionGrowthPct -
                      (a.cpiAssumption ?? DEFAULT_CPI_ASSUMPTION),
                    1,
                  )}{" "}
                  real — affordability erosion of{" "}
                  {medianHH > 0
                    ? Fmt.pct(
                        (result.years[result.years.length - 1]?.effectiveFee ??
                          0) / medianHH,
                        0,
                      )
                    : "—"}{" "}
                  by Y{a.modelYears} if incomes track CPI.
                </>
              );
            })()}
          </div>
        </div>
      </Section>

      <Section className="report-section" eyebrow="11" title="Risk register">
        <div className="grid g-2">
          {buildDealRisks(active, result).map((r) => (
            <div className="card elevated card-pad-lg" key={r.name}>
              <div className="row between" style={{ alignItems: "flex-start" }}>
                <div className="row-label" style={{ maxWidth: "70%" }}>
                  {r.name}
                </div>
                <div className="row gap-2">
                  <span
                    className={`pill ${r.likelihood === "High" ? "danger" : r.likelihood === "Medium" ? "warn" : "success"}`}
                  >
                    {r.likelihood} chance
                  </span>
                  <span
                    className={`pill ${r.impact === "High" ? "danger" : r.impact === "Medium" ? "warn" : "success"}`}
                  >
                    {r.impact} impact
                  </span>
                </div>
              </div>
              <div className="grid g-2" style={{ marginTop: 14, gap: 12 }}>
                <div>
                  <div className="eyebrow">Proposed owner</div>
                  <p className="p-small">{r.proposedOwner}</p>
                </div>
                <div>
                  <div className="eyebrow">Due / decision gate</div>
                  <p className="p-small">{r.dueGate}</p>
                </div>
                <div>
                  <div className="eyebrow">Current control / evidence</div>
                  <p className="p-small">{r.currentControl}</p>
                </div>
                <div>
                  <div className="eyebrow">Provisional residual</div>
                  <p className="p-small">{r.residualRating}</p>
                </div>
              </div>
              <div style={{ marginTop: 6 }}>
                <div className="eyebrow">Escalation trigger</div>
                <p className="p-small">{r.escalationTrigger}</p>
              </div>
              <div style={{ marginTop: 6 }}>
                <div className="eyebrow">Proposed action</div>
                <p className="p-small" style={{ marginBottom: 0 }}>
                  {r.mitigation}
                </p>
              </div>
            </div>
          ))}
        </div>
        <p className="p-small" style={{ marginTop: 10 }}>
          Likelihood is an indicative model read, not an expert assessment.
          Owners, gates and actions are proposed for Board review and are not
          represented as agreed, executed or independently verified.
        </p>
      </Section>

      <Section
        className="report-section"
        eyebrow="12"
        title="Key assumptions on record"
      >
        <div className="grid g-3">
          <KVCard
            title="Term & financing"
            rows={[
              ["Lease term", `${a.leaseTermYears} years`],
              ["Model horizon", `${a.modelYears} years`],
              ["Build period", `${a.developmentMonths} months`],
              ["Holding interest", Fmt.pct(a.holdingInterestRate, 1)],
              ["Operator discount", Fmt.pct(a.discountRate, 1)],
              [
                "Stated ungeared project hurdle",
                Fmt.pct(a.developerHurdleIRR, 1),
              ],
              ["Cap rate (exit)", Fmt.pct(a.capRate, 2)],
              [
                "Min cash reserve",
                `${a.minimumCashReserveMonths} months fixed opex`,
              ],
            ]}
          />
          <KVCard
            title="Demand"
            rows={[
              [
                "Total capacity",
                Fmt.num(result.campus.totalEnrolmentCapacity, 0),
              ],
              ["Opening utilisation", Fmt.pct(a.startingUtilisation, 0)],
              [
                "Steady-state utilisation",
                Fmt.pct(a.steadyStateUtilisation, 0),
              ],
              ["Ramp years", Fmt.years(a.rampYears)],
              ["Tuition growth", Fmt.pct(a.tuitionGrowthPct, 1)],
              [
                "Blended fee Y1",
                Fmt.fullCurrency(result.campus.blendedFeeYear1),
              ],
            ]}
          />
          <KVCard
            title="Operator opex"
            rows={[
              [
                "Variable / student",
                Fmt.fullCurrency(a.variableCostPerStudent),
              ],
              ["Fixed opex Y1", Fmt.fullCurrency(a.fixedOpex)],
              ["Opex growth", Fmt.pct(a.fixedOpexGrowth, 1)],
              ["Fitout allowance", Fmt.fullCurrency(a.reddamFitoutAllowance)],
              ["Amortisation", Fmt.years(a.fitoutAmortisationYears)],
              [rentEscalationLabel, Fmt.pct(Model.effectiveRentGrowth(a), 1)],
            ]}
          />
        </div>
      </Section>
    </div>
  );
}
